import React, { useEffect, useState, useCallback } from "react";
import { useWizard } from "./WizardContext";

const OVERLAY_COLOR = "rgba(0,0,0,0.6)";
const POPUP_WIDTH = 320;
const POPUP_GAP = 12;

function getTargetRect(target) {
  const el = document.querySelector(`[data-wizard="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function calcPopupStyle(rect, position, vpW, vpH) {
  if (!rect) return { top: vpH / 2 - 100, left: vpW / 2 - POPUP_WIDTH / 2 };

  const { top, left, width, height } = rect;
  const centerX = Math.min(Math.max(left + width / 2 - POPUP_WIDTH / 2, 8), vpW - POPUP_WIDTH - 8);

  if (position === "top") {
    return { top: Math.max(top - POPUP_GAP - 220, 8), left: centerX };
  }
  if (position === "right") {
    return { top: Math.max(top + height / 2 - 110, 8), left: Math.min(left + width + POPUP_GAP, vpW - POPUP_WIDTH - 8) };
  }
  if (position === "left") {
    return { top: Math.max(top + height / 2 - 110, 8), left: Math.max(left - POPUP_WIDTH - POPUP_GAP, 8) };
  }
  const belowTop = top + height + POPUP_GAP;
  if (belowTop + 220 > vpH) {
    return { top: Math.max(top - POPUP_GAP - 220, 8), left: centerX };
  }
  return { top: belowTop, left: centerX };
}

export default function WizardOverlay() {
  const { activeSession, activeStepIndex, nextStep, skipSession } = useWizard();
  const [rect, setRect] = useState(null);
  const [vpSize, setVpSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [waitingTargetFound, setWaitingTargetFound] = useState(false);

  const step = activeSession ? activeSession.steps[activeStepIndex] : null;
  const isWaiting = Boolean(step?.waiting_target || step?.next_target);

  // Reset quando muda de step
  useEffect(() => {
    setWaitingTargetFound(false);
  }, [activeStepIndex]);

  const measure = useCallback(() => {
    if (!step) return;
    const activeTarget = (isWaiting && waitingTargetFound)
      ? step.waiting_target
      : step.target;
    setRect(getTargetRect(activeTarget));
    setVpSize({ w: window.innerWidth, h: window.innerHeight });
  }, [step, isWaiting, waitingTargetFound]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // Polling waiting_target: apareceu → destaca e habilita "Próximo"
  useEffect(() => {
    if (!isWaiting || !step?.waiting_target) return;

    const target = step.waiting_target;
    const interval = setInterval(() => {
      setWaitingTargetFound(Boolean(document.querySelector(`[data-wizard="${target}"]`)));
    }, 300);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex, isWaiting]);

  // Polling next_target: apareceu → avança automaticamente
  useEffect(() => {
    if (!step?.next_target) return;

    const target = step.next_target;
    const interval = setInterval(() => {
      if (document.querySelector(`[data-wizard="${target}"]`)) {
        clearInterval(interval);
        nextStep();
      }
    }, 300);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex]);

  if (!activeSession || !step) return null;

  const { w: vpW, h: vpH } = vpSize;
  const highlight = rect || { top: vpH / 2 - 40, left: vpW / 2 - 80, width: 160, height: 80 };
  const { top, left, width, height } = highlight;
  const PADDING = 6;
  const hTop = top - PADDING;
  const hLeft = left - PADDING;
  const hW = width + PADDING * 2;
  const hH = height + PADDING * 2;

  const popupStyle = calcPopupStyle(rect, step.position, vpW, vpH);
  const total = activeSession.steps.length;
  const isLast = activeStepIndex === total - 1;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none" }}>
      {/* top */}
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: hTop, background: OVERLAY_COLOR, pointerEvents: "all" }} />
      {/* bottom */}
      <div style={{ position: "fixed", top: hTop + hH, left: 0, width: "100%", bottom: 0, background: OVERLAY_COLOR, pointerEvents: "all" }} />
      {/* left */}
      <div style={{ position: "fixed", top: hTop, left: 0, width: hLeft, height: hH, background: OVERLAY_COLOR, pointerEvents: "all" }} />
      {/* right */}
      <div style={{ position: "fixed", top: hTop, left: hLeft + hW, right: 0, height: hH, background: OVERLAY_COLOR, pointerEvents: "all" }} />

      {/* highlight border */}
      <div
        style={{
          position: "fixed",
          top: hTop,
          left: hLeft,
          width: hW,
          height: hH,
          borderRadius: 6,
          boxShadow: "0 0 0 3px rgba(216,78,43,0.9)",
          pointerEvents: "none",
        }}
      />

      {/* popup */}
      <div
        style={{
          position: "absolute",
          top: popupStyle.top,
          left: popupStyle.left,
          width: POPUP_WIDTH,
          background: "#fff",
          borderRadius: 8,
          padding: "16px 20px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
          pointerEvents: "all",
          zIndex: 10000,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a", flex: 1 }}>{step.title}</span>
          <span style={{ fontSize: 12, color: "#888", marginLeft: 8, whiteSpace: "nowrap" }}>
            {activeStepIndex + 1} / {total}
          </span>
        </div>

        <p style={{ fontSize: 14, color: "#444", margin: "0 0 16px", lineHeight: 1.5 }}>{step.body}</p>

        {/* progress dots */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {activeSession.steps.map((_, i) => (
            <div
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: i === activeStepIndex ? "rgb(216,78,43)" : "#ddd",
              }}
            />
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={skipSession}
            style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 13, padding: 0 }}
          >
            Pular tutorial
          </button>

          {isWaiting && !waitingTargetFound ? (
            <span style={{ fontSize: 13, color: "#aaa", fontStyle: "italic" }}>
              aguardando sua ação...
            </span>
          ) : (
            <button
              onClick={nextStep}
              style={{
                background: "rgb(216,78,43)",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "8px 18px",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {isLast ? "Concluir" : "Próximo →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
